import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryConstraint,
} from "firebase/firestore";
import type { LgsApplication } from "@/types/lgsApplication";
import { Button } from "@/components/ui/button";

type StatusFilter = "submitted" | "approved" | "rejected";

export default function AdminLgsPage() {
  // --- Admin gate (prevents blank page) ---
  const [authReady, setAuthReady] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [gateErr, setGateErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      try {
        if (!u) {
          setIsAdmin(false);
          setAuthReady(true);
          return;
        }
        const tok = await u.getIdTokenResult(true);
        setIsAdmin(!!(tok.claims as any)?.isAdmin);
        setAuthReady(true);
      } catch (e: any) {
        setGateErr(e?.message || String(e));
        setIsAdmin(false);
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  if (!authReady) {
    return (
      <div className="p-6 text-sm text-slate-300">
        Loading admin…
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          Not authorized. Admin access required.
          {gateErr ? <div className="mt-2 text-xs opacity-80">{gateErr}</div> : null}
        </div>
      </div>
    );
  }

  // Filters and data
  const [filter, setFilter] = useState<StatusFilter>("submitted");
  const [rows, setRows] = useState<Array<LgsAppRow>>([]);
  const [selected, setSelected] = useState<LgsAppRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const { toast } = safeToast();

  // Lab: direct promotion
  const [labUid, setLabUid] = useState("");
  const [labAck, setLabAck] = useState(""); // require typing LAB to confirm
  const [busyLab, setBusyLab] = useState(false);

  async function promoteDirect() {
    if (!labUid) return;
    setBusyLab(true);
    try {
      // Backend should implement: callable that sets custom claim and mirrors user doc
      // e.g. functions.https.onCall(async ({ uid }) => setCustomUserClaims(uid, { isLGS:true }) ...)
      const call = httpsCallable(functions, "adminPromoteToLGS");
      await call({ uid: labUid });
      toast("Promoted. Ask the user to refresh session.");
      setLabAck("");
    } catch (e: any) {
      toast(`Failed: ${e?.message || String(e)}`);
    } finally {
      setBusyLab(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    const base = collection(db, "lgs_applications");
    const cons: QueryConstraint[] = [where("status", "==", filter), orderBy("updatedAt", "desc")];
    const unsub = onSnapshot(query(base, ...cons), (snap) => {
      const list = snap.docs.map((d) => norm(d.id, d.data()));
      setRows(list);
      if (selected) setSelected(list.find((x) => x.id === selected.id) || null);
      setLoading(false);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Actions
  async function doApprove() {
    if (!selected) return;
    setBusy("approve");
    try {
      const call = httpsCallable(functions, "reviewLgsApplication");
      await call({ uid: selected.uid, action: "approve" });
      toast("Application approved.");
      setReason("");
    } catch (e: any) {
      toast(`Approve failed: ${e?.message || String(e)}`);
    } finally {
      setBusy(null);
    }
  }
  async function doReject() {
    if (!selected) return;
    setBusy("reject");
    try {
      const call = httpsCallable(functions, "reviewLgsApplication");
      await call({ uid: selected.uid, action: "reject", reason: reason || null });
      toast("Application rejected.");
      setReason("");
    } catch (e: any) {
      toast(`Reject failed: ${e?.message || String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  const counts = useMemo(() => rows.length, [rows.length]);

  // --- Never return null below this line ---
  return (
    <div className="p-6">
      {/* Debug banner so the body is never empty */}
      <div className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200">
        Admin LGS page · authReady: {String(authReady)} · isAdmin: {String(isAdmin)}
      </div>

      {!authReady && <div className="text-sm text-slate-300">Checking admin access…</div>}

      {authReady && isAdmin === false && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          Not authorized. Admin access required.
        </div>
      )}

      {authReady && isAdmin === true && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-white">LGS Admin Console</h1>
            {import.meta.env.DEV && (
              <span className="text-xs text-slate-400">ENV: dev</span>
            )}
          </div>

          {/* LAB tool: Promote to LGS (direct) — unsafe for production */}
          <LabPromoteDirect />

          {/* Placeholder body so page is not blank while you wire data tables */}
          <div className="rounded-md border border-slate-700 bg-black/30 p-4 text-sm text-slate-300">
            Admin tools will appear here (applications list, detail panel, etc.).
            If this box renders, routing and gating are OK.
          </div>
        </div>
      )}
    </div>
  );
}

// Simple LAB component kept inside this file to guarantee visible content
function LabPromoteDirect() {
  const [uid, setUid] = React.useState("");
  const [ack, setAck] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  async function promote() {
    setMsg(null);
    setBusy(true);
    try {
      const fns = getFunctions();
      const call = httpsCallable(fns, "adminPromoteToLGS");
      await call({ uid });
      setMsg("Promoted. Ask the user to refresh their session.");
    } catch (e: any) {
      setMsg(e?.message || "Failed to promote.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4">
      <div className="mb-2 text-sm font-semibold text-red-200">
        Lab only: Promote to LGS (direct)
      </div>
      <div className="text-xs text-red-200/80">
        Unsafe for production — sets isLGS without the application flow.
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div className="grid gap-1 sm:col-span-2">
          <label className="text-[11px] text-red-100/80">Test account UID</label>
          <input
            value={uid}
            onChange={(e) => setUid(e.target.value.trim())}
            placeholder="uid_123..."
            className="w-full rounded-md border border-red-500/30 bg-transparent px-3 py-2 text-sm text-red-50 placeholder-red-200/40"
          />
        </div>
        <div className="grid gap-1">
          <label className="text-[11px] text-red-100/80">Type LAB to confirm</label>
          <input
            value={ack}
            onChange={(e) => setAck(e.target.value.trim().toUpperCase())}
            placeholder="LAB"
            className="w-full rounded-md border border-red-500/30 bg-transparent px-3 py-2 text-sm text-red-50 placeholder-red-200/40"
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={promote}
          disabled={busy || !uid || ack !== "LAB"}
          className="rounded-md border border-red-500/40 bg-red-500/20 px-3 py-2 text-sm text-red-50 hover:bg-red-500/25 disabled:opacity-60"
        >
          {busy ? "Promoting…" : "Promote to LGS (direct)"}
        </button>
        {msg && <span className="text-xs text-red-200/90">{msg}</span>}
      </div>
    </div>
  );
}

// Types and helpers
type LgsAppRow = (LgsApplication & { id: string });

function norm(id: string, d: DocumentData): LgsAppRow {
  const createdAt = typeof d.createdAt === "number" ? d.createdAt : d.createdAt?.toMillis?.() ?? Date.now();
  const updatedAt = typeof d.updatedAt === "number" ? d.updatedAt : d.updatedAt?.toMillis?.() ?? createdAt;
  return { id, ...(d as any), createdAt, updatedAt } as any;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left font-medium">{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 align-top ${className}`}>{children}</td>;
}
function Row({ label, value, link }: { label: string; value?: string; link?: boolean }) {
  const v = value || "—";
  return (
    <div className="flex items-start gap-3">
      <div className="w-32 shrink-0 text-slate-400">{label}</div>
      <div className="text-slate-200 break-all">
        {link && value ? <a href={value} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">{v}</a> : v}
      </div>
    </div>
  );
}
function Checks({ flags }: { flags: { emailVerified: boolean; domainMatches: boolean; websiteReachable: boolean } }) {
  return (
    <div className="flex items-center gap-2">
      <Badge ok={flags.emailVerified}>Email</Badge>
      <Badge ok={flags.domainMatches}>Domain</Badge>
      <Badge ok={flags.websiteReachable}>Site</Badge>
    </div>
  );
}
function Badge({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  const cls = ok
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;
}
function formatWhen(ms?: number) {
  if (!ms) return "—";
  try { return new Date(ms).toLocaleString(); } catch { return "—"; }
}
function FilterTab({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      className={`rounded-full px-3 py-1 text-xs border ${active ? "bg-white/10 text-white border-slate-600" : "text-slate-300 border-slate-800 hover:bg-white/5"}`}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}

// Minimal toast shim
function safeToast() {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require("@/components/ui/use-toast");
    const hook = mod.useToast as () => { toast: (opts: any) => void };
    const { toast } = hook();
    return { toast: (msg: string) => toast({ description: msg }) };
  } catch {
    return { toast: (msg: string) => console.log("[toast]", msg) };
  }
}
